<script lang="ts" setup>
import { useShortCodesStore } from '../../_runtime/stores/shortcodes';
import { ComponentsList } from '../../_runtime/enums/shortcodes';
import type { ShortCodeFormModel } from '../../_runtime/services/ShortCodesService';
import ShortCodesService from '../../_runtime/services/ShortCodesService';
import FormsService from '#layers/studio/app/runtime/services/FormsService';
import DynamicForm from '#layers/studio/app/components/forms/DynamicForm.vue';
import { Definition as RelatedSelf } from '#layers/studio/app/components/forms/blueprints/related-self';
import { Definition as RelatedDocuments } from '#layers/studio/app/components/forms/blueprints/related-documents';
import type { Entry } from '#layers/studio/app/runtime/builder/form/entries/Entry';

const store = useShortCodesStore();
const $attrs = useAttrs();
const { $log } = useLogging();
const { $alert } = useAlerts();
const { $t } = useLocalization();

const formRef = ref<InstanceType<typeof DynamicForm>>();
const currentProperties = (): Record<string, any> => only($attrs, 'class', 'style', 'properties');
const id = computed(() => Number(currentProperties().properties.editShortCodeId || 0));
const form = reactive(Form.create<ShortCodeFormModel>({
    id: 0,
    name: '',
    displayName: {},
    document: null
})) as FormType<ShortCodeFormModel>;
const messageAlert = ref(null);
const errors = ref(false);

const emit = defineEmits<{
    (e: 'closeModal', id: number, name: string, formId: number, typeId: number): void;
    (e: 'removeShortCode', id: number): void;
}>();

// eslint-disable-next-line
let validateDocument: () => boolean = null;

// eslint-disable-next-line
let collectDocument = async (container: string, payload: Record<string, any>): Promise<Entry> => null;

const unavailable = ref<string[]>([
    RelatedSelf.type,
    RelatedDocuments.type
]);

const loadData = async (): Promise<boolean> =>
{
    try
    {
        if (id.value == 0) return true;

        const response = await ShortCodesService.fetchShortCode(id.value);

        form.withData(response);
    }
    catch (ex: any)
    {
        if ('code' in ex && ex.code === 404)
        {
            setErrorAlert(true, $t('[[[Shorcode nie istnieje. Usuń go z edytora.]]]'));
        }

        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });

        return false;
    }

    return true;
};

function setErrorAlert(setError: boolean, text: string)
{
    errors.value = setError;
    messageAlert.value = text;
}

async function loadFormDefinition()
{
    if (!currentProperties().properties.dynamicForm) return;

    try
    {
        const response = await FormsService.fetch(currentProperties().properties.dynamicForm);

        form.withData(response.result as any);
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message)
        });

        return false;
    }

    return true;
}

function getBuilder()
{
    return formRef.value?.getBuilder();
}

function validate()
{
    if (currentProperties().properties.dynamicForm)
    {
        return formRef.value?.validate();
    }

    return true;
}

function collect(container: string, payload: Record<string, any>)
{
    return formRef.value?.collect(container, payload);
}

const onSubmit = async (): Promise<void> =>
{
    try
    {
        form.wait();

        const errors = form.validate(schema => ({
            name: schema.string().required($t('[[[Pole "Nazwa" jest wymagane.]]]')),
            ...form.$validators
        }), false);

        if (errors != null)
        {
            form.$errors.record(errors);

            return;
        }

        form.id = currentProperties().properties.editShortCodeId;

        if (form.id == 0)
        {
            let response = await ShortCodesService.createShortCode(currentProperties().properties.id, form.except(['document']));

            form.withData(response);

            const document = await formRef.value.collect('shortcodes', { id: form.id });

            response = await ShortCodesService.updateShortCode(form.id, {
                ...form.except(['document']),
                document
            });

            form.withData(response);

            $alert.success($t('[[[ShortCode został dodany.]]]'));
        }
        else
        {
            const document = await formRef.value.collect('shortcodes', { id: form.id });
            const response = await ShortCodesService.updateShortCode(form.id, {
                ...form.except(['document']),
                document
            });

            form.withData(response);

            $alert.success($t('[[[ShortCode został zaktualizowany.]]]'));
        }

        currentProperties().properties.editShortCodeId = form.id;
    }
    catch (ex)
    {
        handleException($log, ex, {
            400: (ex: any) => $alert.warning(ex.message),
            422: (ex: any) =>
            {
                $alert.danger($t('[[[Wystąpiły błędy w formularzu. Sprawdź wszystkie pola formularza.]]]'));
                form.$errors.record(ex.data.errors);
            }
        });
    }
    finally
    {
        form.continue();
    }
};

onMounted(async () =>
{
    await form.ready([
        loadData(),
        loadFormDefinition()
    ]);
});

function goToShortCodesList()
{
    store.modalSection = ComponentsList.List;
    setErrorAlert(false, '');
}

function goToShortCodesGrid()
{
    store.modalSection = ComponentsList.Grid;
    setErrorAlert(false, '');
}

function handleClickChooseShortCode()
{
    goToShortCodesGrid();
    emit('closeModal', currentProperties().properties.editShortCodeId, form.name, currentProperties().properties.dynamicForm, currentProperties().properties.id);
}

function handleClickDeleteShortCode()
{
    goToShortCodesGrid();
    emit('removeShortCode', currentProperties().properties.editShortCodeId);
}
</script>

<template>
    <form v-bind="currentProperties()" @input="form.$errors.clear($event.target.name);">
        <PlaceholderForm :loading="form.loading()">
            <DataCard>
                <!-- Toolbar -->
                <template #toolbar>
                    <div class="toolbar-buttons">
                        <ActionButton variant="secondary" icon="fa-solid fa-grid" :text="$t('[[[Wzory]]]')" @click.prevent="goToShortCodesGrid" />
                        <ActionButton variant="outline-dark" icon="fa-regular fa-rectangle-list" :text="$t('[[[Lista]]]')" :disabled="errors" @click.prevent="goToShortCodesList" />
                        <ActionButton variant="primary" icon="fas fa-check" :text="$t('[[[Zapisz]]]')" :disabled="errors" @click.prevent="onSubmit()" />
                        <ActionButton variant="success" icon="far fa-fw fa-rectangle-code" :text="$t('[[[Wstaw do edytora]]]')" :disabled="errors" @click.prevent="handleClickChooseShortCode()" />
                    </div>
                </template>
                <!-- Formularz -->
                <template #default>
                    <IdeoAlert v-if="messageAlert" show variant="danger" class="d-flex align-items-center justify-content-between mt-4">
                        <div>
                            <i class="fas fa-fw fa-exclamation-triangle me-2" />
                            {{ messageAlert }}
                        </div>
                        <ActionButton variant="danger" icon="fas fa-times" :text="$t('[[[Usuń]]]')" @click.prevent="handleClickDeleteShortCode()" />
                    </IdeoAlert>
                    <div v-else class="row">
                        <div class="col-12">
                            <IdeoFormGroup :label="$t('[[[Nazwa shortcode]]]')" :invalid-feedback="form.$errors.first('name')" :state="form.$errors.state('name')" required>
                                <IdeoFormInput v-model="form.name" type="text" name="name" />
                            </IdeoFormGroup>
                            <div>
                                <div class="mb-2">
                                    {{ $t('[[[Formularz]]]') }}
                                </div>
                                <hr>

                                <template v-if="form.loaded()">
                                    <IdeoAlert v-if="!currentProperties()?.properties?.dynamicForm" show variant="danger">
                                        <div>
                                            <i class="fas fa-fw fa-exclamation-triangle me-2" />
                                            {{ $t('[[[Formularz nie istnieje.]]]') }}
                                        </div>
                                    </IdeoAlert>
                                    <div v-else>
                                        <DynamicForm
                                            ref="formRef"
                                            v-model:document="form.document"
                                            v-model:validate="validateDocument"
                                            v-model:collect="collectDocument"
                                            :form-id="Number(currentProperties().properties.dynamicForm)"
                                            :unavailable="unavailable"
                                        />
                                    </div>
                                </template>
                                <div v-else class="loader-container">
                                    <Loader />
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </DataCard>
        </PlaceholderForm>
    </form>
</template>

<style scoped lang="scss">
:deep(.card-form) {
    border-width: 0;
    padding: 0;
}
:deep(.card-form .panel) {
    padding: 0;
}
.toolbar-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;

    & > .btn {
        margin-right: 0;
    }
}
.loader-container {
    display: flex;
    justify-content: center;
}
</style>
